import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { fetchAllUsers } from '../slices/authSlice';
import {
  exportReportToPDF,
  exportReportToExcel,
  exportAllReportsToPDF,
  exportAllReportsToExcel,
  ReportDayRow,
  UserReportExportData,
} from '../utils/reportExportUtils';
import { formatTime } from '../utils/formatUtils';
import { apiUrl } from '../config/api';

interface IWorkSchedule {
  userId: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  hours?: number;
  mode?: 'fixed' | 'flex';
  validFrom?: string;
  validTo?: string;
}

interface ITimeEntry {
  userId: string;
  startTime: string;
  endTime?: string;
  pausedDuration: number;
  breakMinutes: number;
  type?: string;
}

interface MonthlySummary {
  month: string;
  plannedHours: number;
  totalHours: number;
  overtime: number;
  undertime: number;
}

interface WeeklySummary {
  week: string;
  plannedHours: number;
  totalHours: number;
  overtime: number;
  undertime: number;
}

interface ReportExportOptions {
  monthly: boolean;
  weekly: boolean;
  daily: boolean;
}

const ReportsPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const token = useSelector((state: RootState) => state.auth.token);
  const allUsers = useSelector((state: RootState) => state.auth.allUsers || []);
  const isAdmin = user?.role === 'admin';

  const [selectedUserId, setSelectedUserId] = useState(user?._id || '');

  const [mode, setMode] = useState<'month' | 'range'>('month');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  });

  const [exportFormat, setExportFormat] = useState<'pdf' | 'excel'>('pdf');
  const [exportOptions, setExportOptions] = useState<ReportExportOptions>({
    monthly: true,
    weekly: true,
    daily: true,
  });

  const [reportData, setReportData] = useState<{
    workSchedule: IWorkSchedule[];
    timeEntries: ITimeEntry[];
    totalHours: number;
    plannedHours: number;
    overtime: number;
    undertime: number;
    dailyRows?: ReportDayRow[];
    monthlySummaries?: MonthlySummary[];
    weeklySummaries?: WeeklySummary[];
  } | null>(null);

  useEffect(() => {
    if (isAdmin) dispatch(fetchAllUsers());
  }, [dispatch, isAdmin]);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedUserId || !token) return;

      if (selectedUserId === 'all') {
        setReportData(null);
        return;
      }

      if (mode === 'range' && (!fromDate || !toDate)) return;

      try {
        let url = '';

        if (mode === 'month') {
          if (!selectedMonth) return;
          url = apiUrl(`/api/reports?filter=month&date=${selectedMonth}&userId=${selectedUserId}`);
        }

        if (mode === 'range') {
          url = apiUrl(`/api/reports?filter=range&from=${fromDate}&to=${toDate}&userId=${selectedUserId}`);
        }

        if (!url) return;

        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          const err = await res.json();
          console.error('Backend Fehler:', err);
          return;
        }

        const data = await res.json();
        setReportData(data);
      } catch (err) {
        console.error('Fehler beim Laden des Berichts:', err);
      }
    };

    fetchData();
  }, [mode, selectedMonth, fromDate, toDate, selectedUserId, token]);

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedMonth(e.target.value);
  };

  const handleUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedUserId(e.target.value);
  };

  const handleExportOptionChange = (option: keyof ReportExportOptions) => {
    setExportOptions((current) => ({
      ...current,
      [option]: !current[option],
    }));
  };

  const hasSelectedExportContent =
    exportOptions.monthly || exportOptions.weekly || exportOptions.daily;

  const selectedUserName =
    selectedUserId === 'all'
      ? 'Alle Nutzer'
      : allUsers.find((u) => u._id === selectedUserId)?.username ||
        user?.username ||
        'Unbekannt';

  const exportLabel =
    mode === 'month'
      ? selectedMonth
      : `${fromDate} bis ${toDate}`;

  const hasRealEntries = (entries: ITimeEntry[]) => {
    return entries.some(
      (entry) =>
        entry.type === 'work' ||
        entry.type === 'sick' ||
        entry.type === 'vacation' ||
        !entry.type
    );
  };

  const fetchSingleUserReport = async (targetUserId: string) => {
    if (!token) return null;

    let url = '';

    if (mode === 'month') {
      if (!selectedMonth) return null;
      url = apiUrl(`/api/reports?filter=month&date=${selectedMonth}&userId=${targetUserId}`);
    }

    if (mode === 'range') {
      if (!fromDate || !toDate) return null;
      url = apiUrl(`/api/reports?filter=range&from=${fromDate}&to=${toDate}&userId=${targetUserId}`);
    }

    if (!url) return null;

    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      return null;
    }

    return await res.json();
  };

  const handleExport = async () => {
    if (mode === 'range' && (!fromDate || !toDate)) return;
    if (!hasSelectedExportContent) {
      alert('Bitte mindestens eine Berichtsoption auswählen.');
      return;
    }

    if (selectedUserId === 'all') {
      const reports: UserReportExportData[] = [];

      for (const currentUser of allUsers) {
        const data = await fetchSingleUserReport(currentUser._id);

        if (!data) continue;
        if (!data.timeEntries || !Array.isArray(data.timeEntries)) continue;
        if (!hasRealEntries(data.timeEntries)) continue;

        reports.push({
          username: currentUser.username,
          entries: data.timeEntries,
          summary: {
            plannedHours: data.plannedHours,
            totalHours: data.totalHours,
            overtime: data.overtime,
            undertime: data.undertime,
          },
          monthlySummaries: data.monthlySummaries || [],
          weeklySummaries: data.weeklySummaries || [],
          schedule: data.workSchedule || [],
          dailyRows: data.dailyRows || [],
        });
      }

      if (reports.length === 0) {
        alert('Keine passenden Nutzer mit Zeiteinträgen gefunden.');
        return;
      }

      if (exportFormat === 'pdf') {
        await exportAllReportsToPDF(exportLabel, reports, exportOptions);
      } else {
        exportAllReportsToExcel(exportLabel, reports, exportOptions);
      }

      return;
    }

    if (!reportData) return;

    const entries = reportData.timeEntries;
    const summary = {
      plannedHours: reportData.plannedHours,
      totalHours: reportData.totalHours,
      overtime: reportData.overtime,
      undertime: reportData.undertime,
    };
    const monthlySummaries = reportData.monthlySummaries || [];
    const weeklySummaries = reportData.weeklySummaries || [];
    const schedule = reportData.workSchedule;
    const dailyRows = reportData.dailyRows || [];

    if (exportFormat === 'pdf') {
      await exportReportToPDF(
        selectedUserName,
        exportLabel,
        entries,
        summary,
        schedule,
        {
          ...exportOptions,
          monthlySummaries,
          weeklySummaries,
          dailyRows,
        }
      );
    } else {
      exportReportToExcel(
        selectedUserName,
        exportLabel,
        entries,
        summary,
        schedule,
        {
          ...exportOptions,
          monthlySummaries,
          weeklySummaries,
          dailyRows,
        }
      );
    }
  };

  return (
    <div className="card p-3">
      <h2>Bericht</h2>

      <div className="mb-3">
        <label>Modus auswählen:</label>
        <select
          className="form-select"
          value={mode}
          onChange={(e) => setMode(e.target.value as 'month' | 'range')}
        >
          <option value="month">Monat</option>
          <option value="range">Zeitraum</option>
        </select>
      </div>

      {mode === 'month' && (
        <div className="mb-3">
          <label>Monat auswählen:</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={handleMonthChange}
            className="form-control"
          />
        </div>
      )}

      {mode === 'range' && (
        <div className="mb-3 d-flex gap-2">
          <div>
            <label>Von:</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="form-control"
            />
          </div>

          <div>
            <label>Bis:</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="form-control"
            />
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="mb-3">
          <label>Nutzer auswählen:</label>
          <select
            value={selectedUserId}
            onChange={handleUserChange}
            className="form-control"
          >
            <option value="all">Alle Nutzer</option>
            {allUsers.map((u) => (
              <option key={u._id} value={u._id}>
                {u.username}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="mb-3 border rounded p-3">
        <div className="fw-semibold mb-2">Erweiterte Exportoptionen</div>
        <div className="d-flex flex-column gap-2">
          <label className="form-check report-export-option">
            <input
              className="form-check-input report-export-checkbox"
              type="checkbox"
              checked={exportOptions.monthly}
              onChange={() => handleExportOptionChange('monthly')}
            />
            <span className="form-check-label">Monatliche Über-/Minusstunden</span>
          </label>
          <label className="form-check report-export-option">
            <input
              className="form-check-input report-export-checkbox"
              type="checkbox"
              checked={exportOptions.weekly}
              onChange={() => handleExportOptionChange('weekly')}
            />
            <span className="form-check-label">Wöchentliche Über-/Minusstunden</span>
          </label>
          <label className="form-check report-export-option">
            <input
              className="form-check-input report-export-checkbox"
              type="checkbox"
              checked={exportOptions.daily}
              onChange={() => handleExportOptionChange('daily')}
            />
            <span className="form-check-label">Tägliche Detailtabelle</span>
          </label>
        </div>
        {!hasSelectedExportContent && (
          <div className="text-danger mt-2" style={{ fontSize: 13 }}>
            Bitte mindestens eine Option auswählen.
          </div>
        )}
      </div>

      {selectedUserId !== 'all' && reportData ? (
        <div>
          <p><strong>Sollstunden:</strong> {formatTime(reportData.plannedHours)}</p>
          <p><strong>Iststunden:</strong> {formatTime(reportData.totalHours)}</p>
          <p><strong>Überstunden:</strong> {formatTime(reportData.overtime)}</p>
          <p><strong>Minusstunden:</strong> {formatTime(reportData.undertime)}</p>

          <div className="mt-4">
            {isAdmin && (
              <p className="mb-2">
                <strong>Bericht exportieren für:</strong> {selectedUserName}
              </p>
            )}

            <div className="d-flex align-items-center mb-3">
              <button
                className="btn btn-primary me-3"
                disabled={mode === 'range' && (!fromDate || !toDate)}
                onClick={handleExport}
              >
                Jetzt exportieren
              </button>

              <select
                className="form-select"
                style={{ width: '150px' }}
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as 'pdf' | 'excel')}
              >
                <option value="pdf">PDF</option>
                <option value="excel">Excel</option>
              </select>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          {selectedUserId === 'all' ? (
            <>
              <p className="mb-2">
                <strong>Bericht exportieren für:</strong> Alle Nutzer
              </p>

              <div className="d-flex align-items-center mb-3">
                <button
                  className="btn btn-primary me-3"
                  disabled={mode === 'range' && (!fromDate || !toDate)}
                  onClick={handleExport}
                >
                  Jetzt exportieren
                </button>

                <select
                  className="form-select"
                  style={{ width: '150px' }}
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as 'pdf' | 'excel')}
                >
                  <option value="pdf">PDF</option>
                  <option value="excel">Excel</option>
                </select>
              </div>
            </>
          ) : (
            <p>Bericht wird geladen...</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
